import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, InputGroup, Modal, Row, Table } from 'react-bootstrap';
import { Copy, Pencil, PlusCircle, Trash } from 'react-bootstrap-icons';
import { LocalizeContext } from 'react-locale-language';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../../helpers/api';
import {
  CUSTOMER_LEVEL_OPTIONS,
  ENDPOINTS,
  IRR_CALCULATOR_KEY,
  PAPER_CALCULATOR_KEY,
  PINUI_BINUI_CALCULATOR_KEY,
  RSU_OPTIONS_KEY
} from '../../../helpers/constants';
import { snakeCase } from 'lodash';
import { formatSignupTag, parseSignupTagInput, signupLinkPrefix, signupPageUrl } from '../../../helpers/global';
import AlertModal from '../../common/AlertModal';
import CircularProgressBar from '../../common/circular-progress';
import HorizontalProgress from '../../common/HorizontalProgress';

const SIGNUP_CALCULATOR_OPTIONS = [
  { labelKey: 'irr', value: IRR_CALCULATOR_KEY },
  { labelKey: 'pinui_binui', value: PINUI_BINUI_CALCULATOR_KEY },
  { labelKey: 'paper_apartment', value: PAPER_CALCULATOR_KEY },
  { labelKey: 'rsu_options', value: RSU_OPTIONS_KEY }
];

const emptyDraft = () => ({
  tagInput: '/',
  level: '0',
  endDay: '',
  levelTo: '',
  allowedCalculators: [],
  showRujumBoard: true,
  showGeneralInfo: true
});

const draftFromConfig = config => ({
  _id: config._id,
  tagInput: formatSignupTag(config),
  level: config.level === 0 || config.level ? String(config.level) : '',
  endDay: config.endDay ? String(config.endDay) : '',
  levelTo: config.levelTo === 0 || config.levelTo ? String(config.levelTo) : '',
  allowedCalculators: config.allowedCalculators || [],
  showRujumBoard: Boolean(config.showRujumBoard),
  showGeneralInfo: Boolean(config.showGeneralInfo)
});

const levelLabel = (value, translate) => {
  const option = CUSTOMER_LEVEL_OPTIONS.find(item => Number(item.value) === Number(value));
  if (!option) return;
  return translate(snakeCase(option.label)) || option.label;
};

const PublicSignupConfigModal = ({ show, draft, saving, onHide, onChange, onSubmit, translate }) => {
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <Modal.Header closeButton={!saving}>
        <Modal.Title className="flex-grow-1 flex items-center">
          <h6 className="mb-0">
            {draft?._id ? translate('edit_public_signup_config') : translate('add_public_signup_config')}
          </h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label className="mid mb-1">{translate('signup_tag')}</Form.Label>
          <InputGroup size="sm" dir="ltr">
            <InputGroup.Text className="text-muted">{signupLinkPrefix()}</InputGroup.Text>
            <Form.Control
              value={draft?.tagInput ?? '/'}
              disabled={saving}
              placeholder="/(tag)"
              onChange={e => onChange({ tagInput: e.target.value.toLowerCase() })}
            />
          </InputGroup>
          <Form.Text className="text-muted">{translate('signup_tag_hint')}</Form.Text>
        </Form.Group>

        <Row>
          <Col xs={12} md={4}>
            <Form.Group className="mb-1">
              <Form.Label className="mid mb-1">{translate('level')}</Form.Label>
              <Form.Select
                size="sm"
                value={draft?.level ?? ''}
                disabled={saving}
                onChange={e => onChange({ level: e.target.value })}
              >
                <option value="">{translate('select_level')}</option>
                {CUSTOMER_LEVEL_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {translate(snakeCase(option.label)) || option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-1">
              <Form.Label className="mid mb-1">{translate('end_day')}</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                min="1"
                value={draft?.endDay ?? ''}
                disabled={saving}
                placeholder="5"
                onChange={e => onChange({ endDay: e.target.value })}
              />
              <Form.Text className="text-muted">{translate('end_day_hint')}</Form.Text>
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-1">
              <Form.Label className="mid mb-1">{translate('level_to')}</Form.Label>
              <Form.Select
                size="sm"
                value={draft?.levelTo ?? ''}
                disabled={saving}
                onChange={e => onChange({ levelTo: e.target.value })}
              >
                <option value="">{translate('select_level')}</option>
                {CUSTOMER_LEVEL_OPTIONS.filter(option => Number(option.value) >= 0).map(option => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={draft?.level !== '' && Number(option.value) === Number(draft?.level)}
                  >
                    {translate(snakeCase(option.label)) || option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3 mt-3">
          <Form.Label className="mid mb-2">{translate('allowed_calculators')}</Form.Label>
          <Row>
            {SIGNUP_CALCULATOR_OPTIONS.map(option => (
              <Col key={option.value} xs={12} md={6}>
                <Form.Check
                  type="checkbox"
                  id={`signup-calc-${option.value}`}
                  className="mb-1"
                  label={translate(option.labelKey)}
                  disabled={saving}
                  checked={draft?.allowedCalculators?.includes(option.value)}
                  onChange={e => {
                    const current = draft?.allowedCalculators || [];
                    onChange({
                      allowedCalculators: e.target.checked
                        ? [...current, option.value]
                        : current.filter(value => value !== option.value)
                    });
                  }}
                />
              </Col>
            ))}
          </Row>
        </Form.Group>

        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="mid mb-2">{translate('rujum')}</Form.Label>
              <Form.Check
                type="checkbox"
                id="signup-show-rujum"
                label={translate('show_rujum')}
                disabled={saving}
                checked={Boolean(draft?.showRujumBoard)}
                onChange={e => onChange({ showRujumBoard: e.target.checked })}
              />
            </Form.Group>
          </Col>
          {Number(draft?.level) > 1 && (
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label className="mid mb-2">{translate('general_information')}</Form.Label>
                <Form.Check
                  type="checkbox"
                  id="signup-show-general-info"
                  label={translate('general_information')}
                  disabled={saving}
                  checked={Boolean(draft?.showGeneralInfo)}
                  onChange={e => onChange({ showGeneralInfo: e.target.checked })}
                />
              </Form.Group>
            </Col>
          )}
        </Row>

        {saving && <HorizontalProgress text={translate('save')} />}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="danger" className="text-white" disabled={saving} onClick={onHide}>
          {translate('cancel')}
        </Button>
        <Button size="sm" variant="success" className="text-white" disabled={saving} onClick={onSubmit}>
          {translate('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const PublicSignupConfigs = ({ readOnlyMode }) => {
  const { translate } = useContext(LocalizeContext);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sortedConfigs = useMemo(
    () => [...configs].sort((a, b) => formatSignupTag(a).localeCompare(formatSignupTag(b))),
    [configs]
  );

  const loadConfigs = async () => {
    setLoading(true);
    setLoadError('');
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.PUBLIC_SIGNUP_CONFIGS_SEARCH
    });
    setLoading(false);

    if (error) {
      setLoadError(error);
      return;
    }

    setConfigs(Array.isArray(response) ? response : []);
  };

  useEffect(() => {
    loadConfigs();
  }, []);

  const updateDraft = patch => setDraft(current => ({ ...current, ...patch }));

  const validateDraft = () => {
    const tag = parseSignupTagInput(draft.tagInput);

    if (tag !== '/' && /[\s/]/.test(tag)) {
      toast.error(translate('signup_tag_not_valid'));
      return null;
    }

    if (configs.some(config => config.tag === tag && config._id !== draft._id)) {
      toast.error(translate('signup_tag_taken'));
      return null;
    }

    if (draft.level === '') {
      toast.error(translate('select_level'));
      return null;
    }

    const hasEndDay = draft.endDay !== '';
    const hasLevelTo = draft.levelTo !== '';
    if (hasEndDay !== hasLevelTo) {
      toast.error(translate('end_day_and_level_to_together'));
      return null;
    }
    if (hasEndDay && (!Number.isInteger(Number(draft.endDay)) || Number(draft.endDay) < 1)) {
      toast.error(translate('end_day_min'));
      return null;
    }
    if (hasLevelTo && Number(draft.levelTo) === Number(draft.level)) {
      toast.error(translate('level_and_level_to_must_differ'));
      return null;
    }

    return tag;
  };

  const onSubmit = async () => {
    const tag = validateDraft();
    if (!tag) return;

    const requestBody = {
      tag,
      level: Number(draft.level),
      endDay: draft.endDay === '' ? null : Number(draft.endDay),
      levelTo: draft.levelTo === '' ? null : Number(draft.levelTo),
      allowedCalculators: draft.allowedCalculators || [],
      showRujumBoard: Boolean(draft.showRujumBoard),
      showGeneralInfo: Number(draft.level) > 1 ? Boolean(draft.showGeneralInfo) : false
    };

    setSaving(true);
    const { response, error } = await makeApiRequests({
      endpoint: draft._id ? ENDPOINTS.PUBLIC_SIGNUP_CONFIGS_WITH_ID(draft._id) : ENDPOINTS.PUBLIC_SIGNUP_CONFIGS,
      method: draft._id ? 'PUT' : 'POST',
      requestBody
    });
    setSaving(false);

    if (error) {
      toast.error(error);
      return;
    }

    setConfigs(current =>
      draft._id ? current.map(config => (config._id === response._id ? response : config)) : [response, ...current]
    );
    setDraft(null);
    toast.success(translate('public_signup_config_saved'));
  };

  const onDelete = async () => {
    setDeleting(true);
    const { error } = await makeApiRequests({
      endpoint: ENDPOINTS.PUBLIC_SIGNUP_CONFIGS_WITH_ID(deleteTarget._id),
      method: 'DELETE'
    });
    setDeleting(false);

    if (error) {
      toast.error(error);
      return;
    }

    setConfigs(current => current.filter(config => config._id !== deleteTarget._id));
    setDeleteTarget(null);
    toast.success(translate('public_signup_config_deleted'));
  };

  const copyLink = async config => {
    try {
      await navigator.clipboard.writeText(signupPageUrl(config));
      toast.success(translate('link_copied'));
    } catch (error) {
      toast.error(error?.message || 'Could not copy link');
    }
  };

  return (
    <Card className="my-3">
      <Card.Header className="d-flex align-items-center justify-content-between">
        <span>{translate('public_signup_config')}</span>
        {!readOnlyMode && !loadError && (
          <Button
            size="sm"
            variant="success"
            className="text-white d-inline-flex align-items-center"
            onClick={() => setDraft(emptyDraft())}
          >
            <PlusCircle className="me-1" />
            {translate('add_public_signup_config')}
          </Button>
        )}
      </Card.Header>
      <Card.Body className="p-3">
        <p className="small text-muted mb-3">{translate('public_signup_config_hint')}</p>
        {loading ? (
          <div className="p-4 text-center">
            <CircularProgressBar size={5} />
          </div>
        ) : loadError ? (
          <Alert variant="danger" className="mb-0 small">
            {loadError}
          </Alert>
        ) : sortedConfigs.length === 0 ? (
          <p className="small text-muted mb-0">{translate('no_public_signup_config')}</p>
        ) : (
          <Table responsive size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>{translate('signup_tag')}</th>
                <th>{translate('level')}</th>
                <th>{translate('end_day')}</th>
                <th>{translate('level_to')}</th>
                <th>{translate('allowed_calculators')}</th>
                <th>{translate('show_rujum')}</th>
                <th>{translate('general_information')}</th>
                {!readOnlyMode && <th />}
              </tr>
            </thead>
            <tbody>
              {sortedConfigs.map(config => (
                <tr key={config._id}>
                  <td className="text-nowrap">
                    <span className="d-inline-flex align-items-center">
                      <span className="me-2">{formatSignupTag(config)}</span>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 d-inline-flex align-items-center text-dark"
                        title={translate('copy')}
                        onClick={() => copyLink(config)}
                      >
                        <Copy size={14} />
                      </Button>
                    </span>
                  </td>
                  <td>{levelLabel(config.level, translate) || '_'}</td>
                  <td>{config.endDay || '_'}</td>
                  <td>{config.levelTo === 0 || config.levelTo ? levelLabel(config.levelTo, translate) : '_'}</td>
                  <td>
                    {config.allowedCalculators?.length
                      ? config.allowedCalculators
                          .map(value => {
                            const option = SIGNUP_CALCULATOR_OPTIONS.find(item => item.value === value);
                            return option ? translate(option.labelKey) : value;
                          })
                          .join(', ')
                      : '_'}
                  </td>
                  <td>{config.showRujumBoard ? translate('yes') : translate('no')}</td>
                  <td>{config.showGeneralInfo ? translate('yes') : translate('no')}</td>
                  {!readOnlyMode && (
                    <td className="text-nowrap">
                      <Button
                        size="sm"
                        variant="link"
                        className="p-1"
                        onClick={() => setDraft(draftFromConfig(config))}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-1 text-danger"
                        onClick={() => setDeleteTarget(config)}
                      >
                        <Trash size={14} />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <PublicSignupConfigModal
        show={draft !== null}
        draft={draft}
        saving={saving}
        onHide={() => !saving && setDraft(null)}
        onChange={updateDraft}
        onSubmit={onSubmit}
        translate={translate}
      />
      <AlertModal
        show={deleteTarget !== null}
        alertText={translate('delete_public_signup_config_confirm')}
        showProgress={deleting}
        progressText={`${translate('delete')}...`}
        onHide={() => !deleting && setDeleteTarget(null)}
        onDismissClick={() => !deleting && setDeleteTarget(null)}
        onContinueClick={onDelete}
        continueButtonText={translate('delete')}
        dismissButtonText={translate('cancel')}
      />
    </Card>
  );
};

export default PublicSignupConfigs;
